import { ButtonType, EpicIcons } from "@constellation-academy/epic-ui";
import { useState } from "react";
import { useMutation } from "react-relay";
import { toast } from "react-toastify";
import { Button, ButtonVariant } from "@components/button";
import { useTranslation } from "@i18n/hooks";
import { supabase } from "@network/supabase-client/supabase-client";
import {
	impersonateUserButton_loginAsUserJwtMutation,
	impersonateUserButton_loginAsUserJwtMutation$data,
} from "@relay/impersonateUserButton_loginAsUserJwtMutation.graphql";
import { LOGIN_AS_USER_JWT_MUTATION } from "./impersonate-user-button.graphql";
import { webBase } from "../../../environment";

type JwtTokenData = NonNullable<
	impersonateUserButton_loginAsUserJwtMutation$data["Admin"]["Auth"]["loginAsUserJwt"]
>["jwtTokens"];

export const ImpersonateUserButton = ({ userId }: { userId: string }) => {
	const { t } = useTranslation("common");
	const [isLoading, setIsLoading] = useState(false);
	const [loginAsUserJwt] = useMutation<impersonateUserButton_loginAsUserJwtMutation>(
		LOGIN_AS_USER_JWT_MUTATION,
	);

	const impersonateOnClickHandler = (userId: string) => () => {
		setIsLoading(true);
		loginAsUserJwt({
			variables: {
				input: {
					userId,
					deviceKind: "WebApp",
				},
			},
			onCompleted: (data) => {
				const jwtTokens = data.Admin.Auth.loginAsUserJwt?.jwtTokens;

				if (!jwtTokens) {
					showErrorToast();
					setIsLoading(false);
					return;
				}

				void openUserSessionInNewTab(jwtTokens)
					.then(() => {
						setIsLoading(false);
					})
					.catch(() => {
						showSupabaseTokenErrorToast();
						setIsLoading(false);
					});
			},
			onError() {
				showErrorToast();
				setIsLoading(false);
			},
		});
	};

	const showErrorToast = () => toast.error(t("user.accessTokenError"));

	const showSupabaseTokenErrorToast = () => toast.error(t("user.supabaseTokenError"));

	const openUserSessionInNewTab = async (jwtTokens: JwtTokenData) => {
		const baseUrl = webBase;

		const params = new URLSearchParams({
			impersonationToken: jwtTokens.accessToken,
			refreshToken: jwtTokens.refreshToken,
		});

		// Attach current admin Supabase session tokens - required for impersonation
		const {
			data: { session },
		} = await supabase.auth.getSession();

		const supabaseAccessToken = session?.access_token;
		const supabaseRefreshToken = session?.refresh_token;

		if (!supabaseAccessToken || !supabaseRefreshToken) {
			throw new Error(
				"Supabase session tokens not available. Admin must be logged in with Supabase.",
			);
		}

		params.set("supabaseAccessToken", supabaseAccessToken);
		params.set("supabaseRefreshToken", supabaseRefreshToken);

		const fullUrl = `${baseUrl}?${params.toString()}`;

		window.open(fullUrl, "_blank", "noopener,noreferrer");
	};

	return (
		<Button
			type={ButtonType.Button}
			variant={ButtonVariant.Success}
			onClick={impersonateOnClickHandler(userId)}
			tooltip="Impersonate"
			icon={EpicIcons.USER}
			loading={isLoading}
			disabled={isLoading}
		/>
	);
};
